import React from 'react';
import { cn } from '@/lib/utils';

interface CollectabilityGaugeProps {
  score: number;
  className?: string;
}

export const CollectabilityGauge: React.FC<CollectabilityGaugeProps> = ({ score, className }) => {
  // Calculate percentage (300-850 range mapped to 0-100%)
  const percentage = Math.min(100, Math.max(0, ((score - 300) / 550) * 100));

  // Determine color based on score
  const getScoreColor = (score: number) => {
    if (score >= 700) return { bg: 'bg-green-500', text: 'text-green-500', gradient: 'from-green-400 to-green-600' };
    if (score >= 600) return { bg: 'bg-blue-500', text: 'text-blue-500', gradient: 'from-blue-400 to-blue-600' };
    if (score >= 500) return { bg: 'bg-yellow-500', text: 'text-yellow-500', gradient: 'from-yellow-400 to-yellow-600' };
    return { bg: 'bg-red-500', text: 'text-red-500', gradient: 'from-red-400 to-red-600' };
  };

  const colors = getScoreColor(score);

  return (
    <div className={cn('relative', className)}>
      {/* Gauge Container */}
      <div className="relative w-full aspect-square max-w-xs mx-auto">
        {/* Background Circle */}
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
          {/* Background Arc */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="16"
            strokeDasharray="251.2 251.2"
            strokeDashoffset="125.6"
            strokeLinecap="round"
          />
          {/* Progress Arc */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="url(#gaugeGradient)"
            strokeWidth="16"
            strokeDasharray="251.2 251.2"
            strokeDashoffset={251.2 - (percentage / 100) * 125.6}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className={cn('transition-colors', colors.bg)} stopOpacity="0.8" />
              <stop offset="100%" className={cn('transition-colors', colors.bg)} />
            </linearGradient>
          </defs>
        </svg>

        {/* Score Display - Centered */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-center">
            <div className={cn('text-5xl font-bold mb-1', colors.text)}>{score}</div>
            <div className="text-sm text-slate-500 font-medium">CollectABILITY Score</div>
          </div>
        </div>
      </div>

      {/* Score Range Labels */}
      <div className="flex justify-between text-xs text-slate-500 mt-2 px-4">
        <span>300</span>
        <span className="font-medium text-slate-700">Score Range</span>
        <span>850</span>
      </div>

      {/* Score Interpretation */}
      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <div className={cn('w-3 h-3 rounded-full', colors.bg)} />
          <span className="text-sm font-medium text-slate-900">
            {score >= 700 ? 'Excellent' : score >= 600 ? 'Good' : score >= 500 ? 'Fair' : 'Poor'} CollectABILITY
          </span>
        </div>
        <p className="text-xs text-slate-600">
          {score >= 700
            ? 'High likelihood of successful collection with favorable terms'
            : score >= 600
            ? 'Good collection prospects with structured approach'
            : score >= 500
            ? 'Moderate collection potential - requires strategic planning'
            : 'Challenging collection scenario - consider settlement options'}
        </p>
      </div>
    </div>
  );
};
